import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Search, X } from 'lucide-react';
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Command as CommandPrimitive } from 'cmdk';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { getKeywordSuggestions } from '../../services/apiService';
import debounce from '../../utils/debounce';
import {
  HAS_SEP,
  Op,
  QUERY_SLOT,
  TRAILING_OP_ONLY_RE,
  extractFragment,
  flipOp,
  lastSepEnd,
  parseQuery,
} from './parseQuery';

const KEYWORD_VALUE_PRIMARY = '__keyword_primary__';
const KEYWORD_VALUE_FLIPPED = '__keyword_flipped__';
const SUGGESTION_LIMIT = 3;
const MIN_FRAGMENT_LENGTH = 2;
const SUGGESTIONS_VISIBLE = 6;

export interface SearchBarFilters {
  yearFrom?: number;
  yearTo?: number;
  faculties?: string[];
}

interface GenericSearchBarProps<T extends { id: number | string; name: string }> {
  namespace: 'person' | 'projects' | 'companies';
  destinationPath: string;
  detailPath: (item: T) => string;
  suggestionsHeadingKey: string;
  fetchSuggestions: (query: string) => Promise<{ suggestions?: T[]; error?: string }>;
  renderSuggestionItem: (
    item: T,
    helpers: { highlight: (name: string) => React.ReactNode },
  ) => React.ReactNode;
  filters?: SearchBarFilters;
  onSearch?: (keywords: string[], ops: Op[]) => void;
  onClear?: () => void;
  initialValue?: string;
}

export default function GenericSearchBar<T extends { id: number | string; name: string }>({
  namespace,
  destinationPath,
  detailPath,
  suggestionsHeadingKey,
  fetchSuggestions: fetchEntitySuggestions,
  renderSuggestionItem,
  filters,
  onSearch,
  onClear,
  initialValue,
}: GenericSearchBarProps<T>) {
  const { t } = useTranslation(namespace);
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState(initialValue ?? '');

  useEffect(() => {
    if (initialValue !== undefined) {
      setSearchTerm(initialValue);
    }
  }, [initialValue]);

  const [suggestions, setSuggestions] = useState<T[]>([]);
  const [keywordSuggestions, setKeywordSuggestions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  const fetchSuggestions = async (query: string) => {
    if (!query.trim()) {
      setSuggestions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await fetchEntitySuggestions(query);
      if (data.error) {
        setError(data.error);
        setSuggestions([]);
      } else {
        setSuggestions(data.suggestions || []);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch suggestions');
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetch = useCallback(
    debounce((q: string) => fetchSuggestions(q), 250),
    [],
  );

  const fetchKeywordSuggestions = async (fragment: string) => {
    if (!fragment.trim() || fragment.trim().length < MIN_FRAGMENT_LENGTH) {
      setKeywordSuggestions([]);
      return;
    }
    try {
      const data = await getKeywordSuggestions(fragment.trim());
      setKeywordSuggestions((data.suggestions || []).slice(0, SUGGESTION_LIMIT));
    } catch {
      setKeywordSuggestions([]);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedKeywordFetch = useCallback(
    debounce((f: string) => fetchKeywordSuggestions(f), 200),
    [],
  );

  const inCompetencyMode = HAS_SEP.test(searchTerm);

  const handleChange = (value: string) => {
    setSearchTerm(value);
    setOpen(true);
    if (HAS_SEP.test(value)) {
      setSuggestions([]);
    } else {
      debouncedFetch(value);
    }
    debouncedKeywordFetch(extractFragment(value));
  };

  const goToDetail = (s: T) => {
    setOpen(false);
    navigate(detailPath(s));
  };

  const parsed = useMemo(() => parseQuery(searchTerm), [searchTerm]);

  const commitKeywordSearch = (ops: Op[]) => {
    if (parsed.keywords.length === 0) return;
    setOpen(false);
    if (onSearch) {
      onSearch(parsed.keywords, ops);
      return;
    }
    const params = new URLSearchParams();
    params.set('q', parsed.keywords.join(','));
    if (ops.length > 0) params.set('ops', ops.join(','));
    if (filters?.yearFrom !== undefined) params.set('year_from', String(filters.yearFrom));
    if (filters?.yearTo !== undefined) params.set('year_to', String(filters.yearTo));
    if (filters?.faculties && filters.faculties.length > 0) {
      params.set('faculties', filters.faculties.join(','));
    }
    navigate(`${destinationPath}?${params.toString()}`);
  };

  const flippedOps = parsed.ops.map(flipOp);
  const flippedTrailingOp = parsed.trailingOp ? flipOp(parsed.trailingOp) : undefined;
  const showFlippedHint = parsed.keywords.length >= 2;

  const clear = () => {
    setSearchTerm('');
    setSuggestions([]);
    setKeywordSuggestions([]);
    setError(null);
    setOpen(false);
    inputRef.current?.focus();
    onClear?.();
  };

  const applyKeywordSuggestion = (kw: string) => {
    let next: string;
    if (TRAILING_OP_ONLY_RE.test(searchTerm)) {
      next = `${searchTerm.replace(/\s+$/, '')} ${kw} `;
    } else {
      const head = searchTerm.slice(0, lastSepEnd(searchTerm));
      next = `${head}${kw} `;
    }
    setSearchTerm(next);
    setKeywordSuggestions([]);
    inputRef.current?.focus();
  };

  const trimmed = searchTerm.trim();
  const hasQuery = trimmed.length > 0;
  const showKeywordItem = parsed.keywords.length > 0;
  const fragment = useMemo(() => extractFragment(searchTerm), [searchTerm]);
  const filteredKeywordSuggestions = useMemo(
    () => keywordSuggestions.filter((kw) => kw.toLowerCase() !== fragment.toLowerCase()),
    [keywordSuggestions, fragment],
  );
  const showSuggestions = filteredKeywordSuggestions.length > 0 && fragment.length >= MIN_FRAGMENT_LENGTH;
  const showEntities = !inCompetencyMode && suggestions.length > 0;
  const entitiesVisible = showEntities ? suggestions.slice(0, SUGGESTIONS_VISIBLE) : [];
  const showDropdown = open && hasQuery && (showKeywordItem || showSuggestions || showEntities || loading);
  const showSearchingHint = !inCompetencyMode && loading && suggestions.length === 0;

  const [labelBefore, labelAfter] = useMemo(() => {
    const raw = t('command.searchAsKeyword', { query: QUERY_SLOT });
    const idx = raw.indexOf(QUERY_SLOT);
    if (idx < 0) return [raw, ''];
    return [raw.slice(0, idx), raw.slice(idx + QUERY_SLOT.length)];
  }, [t]);

  const highlight = useMemo(() => {
    return (name: string) => {
      const q = trimmed;
      if (!q) return name;
      const lower = name.toLowerCase();
      const ql = q.toLowerCase();
      const parts: React.ReactNode[] = [];
      let cursor = 0;
      let idx = lower.indexOf(ql, cursor);
      let key = 0;
      while (idx !== -1) {
        if (idx > cursor) parts.push(name.slice(cursor, idx));
        parts.push(
          <mark key={key++} className="bg-transparent text-ctu-text font-semibold">
            {name.slice(idx, idx + q.length)}
          </mark>,
        );
        cursor = idx + q.length;
        idx = lower.indexOf(ql, cursor);
      }
      if (cursor < name.length) parts.push(name.slice(cursor));
      return <>{parts}</>;
    };
  }, [trimmed]);

  return (
    <div className="mb-6">
      <div ref={wrapperRef} className="relative">
        <Command
          shouldFilter={false}
          className={cn(
            'overflow-visible rounded-md border border-ctu-border bg-popover',
            showDropdown && 'shadow-md',
          )}
        >
          <div className="relative flex items-center px-3">
            <Search className="pointer-events-none mr-2 h-4 w-4 shrink-0 text-ctu-text-muted" aria-hidden />
            <CommandPrimitive.Input
              ref={inputRef}
              value={searchTerm}
              onValueChange={handleChange}
              onFocus={() => hasQuery && setOpen(true)}
              placeholder={t('searchPlaceholder')}
              aria-label={t('pageTitle')}
              autoComplete="off"
              className="flex h-11 w-full bg-transparent py-3 pr-8 text-sm outline-none placeholder:text-muted-foreground"
            />
            {loading && !inCompetencyMode ? (
              <Loader2 className="pointer-events-none absolute right-3 h-4 w-4 animate-spin text-ctu-text-muted" aria-hidden />
            ) : searchTerm ? (
              <button
                type="button"
                onClick={clear}
                className="absolute right-2 inline-flex h-7 w-7 items-center justify-center rounded-md text-ctu-text-muted transition-colors hover:bg-muted/60 hover:text-ctu-text"
                aria-label={t('buttons.clearSearch')}
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>

          {showDropdown && (
            <CommandList className="border-t">
              {showKeywordItem && (
                <CommandGroup heading={t('command.keywordHeading')}>
                  <KeywordHint
                    value={KEYWORD_VALUE_PRIMARY}
                    keywords={parsed.keywords}
                    ops={parsed.ops}
                    trailingOp={parsed.trailingOp}
                    labelBefore={labelBefore}
                    labelAfter={labelAfter}
                    onSelect={() => commitKeywordSearch(parsed.ops)}
                  />
                  {showFlippedHint && (
                    <KeywordHint
                      value={KEYWORD_VALUE_FLIPPED}
                      keywords={parsed.keywords}
                      ops={flippedOps}
                      trailingOp={flippedTrailingOp}
                      labelBefore={labelBefore}
                      labelAfter={labelAfter}
                      onSelect={() => commitKeywordSearch(flippedOps)}
                    />
                  )}
                </CommandGroup>
              )}

              {showSuggestions && (
                <CommandGroup heading={t('command.suggestionsHeading')}>
                  {filteredKeywordSuggestions.map((kw) => (
                    <CommandItem
                      key={`kw-${kw}`}
                      value={`kw-${kw}`}
                      onSelect={() => applyKeywordSuggestion(kw)}
                    >
                      <Search className="text-ctu-text-muted" aria-hidden />
                      <Badge variant="info" className="h-5 px-1.5 text-xs font-medium">
                        {kw}
                      </Badge>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {showEntities && (
                <CommandGroup heading={t(suggestionsHeadingKey)}>
                  {entitiesVisible.map((s) => (
                    <CommandItem
                      key={s.id}
                      value={`entity-${s.id}-${s.name}`}
                      onSelect={() => goToDetail(s)}
                    >
                      {renderSuggestionItem(s, { highlight })}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {showSearchingHint && (
                <div className="flex items-center gap-3 px-4 py-3 text-sm text-ctu-text-muted">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                  {t('searching')}
                </div>
              )}
            </CommandList>
          )}
        </Command>
      </div>

      {error && (
        <Alert variant="destructive" className="mt-3">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}

interface KeywordHintProps {
  value: string;
  keywords: string[];
  ops: Op[];
  trailingOp?: Op;
  labelBefore: string;
  labelAfter: string;
  onSelect: () => void;
}

function KeywordHint({ value, keywords, ops, trailingOp, labelBefore, labelAfter, onSelect }: KeywordHintProps) {
  return (
    <CommandItem value={value} onSelect={onSelect}>
      <Search className="text-ctu-text-muted" aria-hidden />
      <span className="flex flex-wrap items-center gap-1">
        {labelBefore && <span>{labelBefore}</span>}
        {keywords.map((kw, i) => (
          <Fragment key={i}>
            {i > 0 && (
              <Badge variant="outline" className="h-5 rounded px-1.5 text-[10px] font-semibold uppercase tracking-wide text-ctu-text-muted">
                {ops[i - 1]}
              </Badge>
            )}
            <Badge variant="info" className="h-5 px-1.5 text-xs font-medium">
              {kw}
            </Badge>
          </Fragment>
        ))}
        {trailingOp && (
          <Badge variant="outline" className="h-5 rounded px-1.5 text-[10px] font-semibold uppercase tracking-wide text-ctu-text-muted">
            {trailingOp}
          </Badge>
        )}
        {labelAfter && <span>{labelAfter}</span>}
      </span>
    </CommandItem>
  );
}
